import React, { useEffect, useRef } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SectionHeading from '../home/SectionHeading';
import { useInView, reveal } from '../home/motion';
import AnimatedIcon from './AnimatedIcon';
import { startSparkleEmitter } from './sparkleEmitter';
import { useT } from '../../i18n/LanguageContext';

// Original marketing copy inspired by the book's own family descriptions —
// deliberately NOT the book's text, and never the per-card prompts (those
// are the book's actual content and stay behind the purchase).
function getFamilies(t) {
  return [
    {
      icon: 'network', name: t('method.families.family1Name'),
      text: t('method.families.family1Text'),
    },
    {
      icon: 'machine', name: t('method.families.family2Name'),
      text: t('method.families.family2Text'),
    },
    {
      icon: 'collab', name: t('method.families.family3Name'),
      text: t('method.families.family3Text'),
    },
    {
      icon: 'robot', name: t('method.families.family4Name'),
      text: t('method.families.family4Text'),
    },
    {
      icon: 'aihead', name: t('method.families.family5Name'),
      text: t('method.families.family5Text'),
    },
  ];
}

const ICON_COLORS = ['coralText', 'cyanText', 'coralText', 'cyanText', 'coralText'];
const ICON_SIZE = 132;
// Spread each icon's loop start so they never all go blank/reset at the
// same instant — at any moment at least one is mid-outline or filled.
const ICON_START_STAGGER_MS = 1100;

// Continuous particle emission (same 4-point star mark as the cursor
// trail — see sparkleEmitter.js), not a single static icon: stars keep
// surfacing at the center and fading outward, forever. Sized/positioned to
// match the icon boxes above it exactly (same size, no row-stretch), so the
// emission point sits centered in the column the same way the icons do.
function SparkleCell() {
  const theme = useTheme();
  const hostRef = useRef(null);

  useEffect(() => startSparkleEmitter(hostRef.current, theme.home.coral), [theme]);

  return (
    <Box
      ref={hostRef}
      aria-hidden="true"
      sx={{ position: 'relative', width: ICON_SIZE, height: ICON_SIZE, mx: 'auto' }}
    />
  );
}

// 5 families on a 3-column grid means the second row only has 2 cards —
// they land in columns 1+2 by default flow. Placed explicitly instead:
// column 1 + column 3, with a decorative sparkle filling column 2. Only
// meaningful at the md+ 3-column breakpoint; hidden (zero footprint) below
// that, where the grid wraps differently and there's no "middle" to fill.
function getCells(families) {
  return [
    { family: families[0], colorKey: ICON_COLORS[0] },
    { family: families[1], colorKey: ICON_COLORS[1] },
    { family: families[2], colorKey: ICON_COLORS[2] },
    { family: families[3], colorKey: ICON_COLORS[3] },
    { sparkle: true },
    { family: families[4], colorKey: ICON_COLORS[4] },
  ];
}

export default function MethodFamilies() {
  const theme = useTheme();
  const t = useT();
  const { ref, visible } = useInView();
  const CELLS = getCells(getFamilies(t));

  return (
    <Box sx={{ bgcolor: theme.home.paperDeep, py: theme.home.sectionPy }}>
      <Box sx={{ maxWidth: theme.home.maxWidth, mx: 'auto', px: { xs: 3, md: 5 } }}>
        <SectionHeading
          eyebrow={t('method.families.eyebrow')}
          title={t('method.families.title')}
          lede={t('method.families.lede')}
        />
        <Grid ref={ref} container spacing={{ xs: 4, md: 5 }} sx={{ mt: { xs: 2, md: 3 } }}>
          {(() => {
            let familyIndex = -1;
            return CELLS.map((cell, i) => {
              if (cell.sparkle) {
                return (
                  <Grid
                    item xs={0} sm={0} md={4} key="sparkle"
                    sx={{ display: { xs: 'none', sm: 'none', md: 'flex' }, alignSelf: 'flex-start', justifyContent: 'center' }}
                  >
                    <SparkleCell />
                  </Grid>
                );
              }
              familyIndex += 1;
              const f = cell.family;
              return (
                <Grid item xs={12} sm={6} md={4} key={f.name} sx={reveal(visible, 0.08 * i)}>
                  <Box sx={{ textAlign: 'center', px: 2 }}>
                    <AnimatedIcon
                      name={f.icon} color={theme.home[cell.colorKey]} size={ICON_SIZE}
                      startDelayMs={familyIndex * ICON_START_STAGGER_MS}
                    />
                    <Typography sx={{
                      mt: 2, fontFamily: theme.home.display, fontWeight: 700,
                      fontSize: '1.15rem', color: theme.home.ink,
                    }}>
                      {f.name}
                    </Typography>
                    <Typography sx={{ mt: 1, color: theme.home.inkMuted, lineHeight: 1.6 }}>
                      {f.text}
                    </Typography>
                  </Box>
                </Grid>
              );
            });
          })()}
        </Grid>
      </Box>
    </Box>
  );
}
